import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';
import './MobileCloudDrivePage.css';

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const MobileCloudDrivePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recent');   // recent | mydrive
  const [activeFilter, setActiveFilter] = useState('all'); // all | file | media

  const fetchFiles = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/messages/cloud-drive/${user.id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setFiles(Array.isArray(data.files) ? data.files : []);
    } catch (_) {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // ── Derived list ─────────────────────────────────────────────────────
  let displayed = [...files];

  // Tab filter
  if (activeTab === 'mydrive') {
    displayed = displayed.filter(f => f.ownerId === user?.id);
  }
  // Recent: already sorted by createdAt desc from backend, just show top 50
  if (activeTab === 'recent') {
    displayed = displayed.slice(0, 50);
  }

  // Type filter
  if (activeFilter === 'file') {
    displayed = displayed.filter(f => f.type === 'file');
  } else if (activeFilter === 'media') {
    displayed = displayed.filter(f => f.type === 'image' || f.type === 'video');
  }

  const handleFileClick = (f) => {
    window.open(getFileUrl(f.fileUrl), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mcd-page">
      {/* ── Header ── */}
      <div className="mcd-header">
        <button
          className="mcd-back-btn"
          onClick={() => navigate('/mobile/messenger')}
          aria-label="Back"
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mcd-title">Cloud drive</h1>
      </div>

      {/* ── Top Tabs (Recent / My Drive) ── */}
      <div className="mcd-top-tabs">
        <button
          className={`mcd-top-tab ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveTab('recent')}
          type="button"
        >
          Recent
        </button>
        <button
          className={`mcd-top-tab ${activeTab === 'mydrive' ? 'active' : ''}`}
          onClick={() => setActiveTab('mydrive')}
          type="button"
        >
          My drive
        </button>
      </div>

      {/* ── Filter Pills (All / Files / Photos & Videos) ── */}
      <div className="mcd-filters">
        {[
          { key: 'all', label: 'All' },
          { key: 'file', label: 'Files' },
          { key: 'media', label: 'Photos & Videos' }
        ].map((f, i) => (
          <React.Fragment key={f.key}>
            {i > 0 && <span className="mcd-filter-sep">/</span>}
            <button
              className={`mcd-filter-pill ${activeFilter === f.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(f.key)}
              type="button"
            >
              {f.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="mcd-content">
        {loading ? (
          <div className="mcd-loading">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="mcd-skel-row">
                <div className="mcd-skel-thumb" />
                <div className="mcd-skel-body">
                  <div className="mcd-skel-name" />
                  <div className="mcd-skel-sub" />
                </div>
              </div>
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="mcd-empty">
            <div className="mcd-empty-illustration">
              <svg viewBox="0 0 160 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="80" cy="112" rx="62" ry="18" fill="#e8f0fe" />
                <rect x="38" y="28" width="84" height="72" rx="10" fill="#dce8ff" />
                <rect x="48" y="18" width="64" height="72" rx="8" fill="#edf3ff" />
                <rect x="57" y="33" width="32" height="5" rx="2.5" fill="#b0c4f8" />
                <rect x="57" y="44" width="46" height="5" rx="2.5" fill="#b0c4f8" />
                <rect x="57" y="55" width="24" height="5" rx="2.5" fill="#b0c4f8" />
                <circle cx="106" cy="54" r="22" fill="#fff3e8" />
                <circle cx="106" cy="46" r="7" fill="#ffb87a" />
                <path d="M91 70c0-8 7-15 15-15s15 7 15 15" fill="#ffb87a" />
                <rect x="108" y="57" width="13" height="20" rx="4" fill="#ff8c00" />
                <rect x="108" y="70" width="13" height="10" rx="2" fill="#ff8c00" />
                <rect x="104" y="66" width="4" height="13" rx="2" fill="#ffb87a" />
                <rect x="121" y="66" width="4" height="10" rx="2" fill="#ffb87a" />
                <rect x="110" y="75" width="4" height="6" rx="2" fill="#ff8c00" />
                <rect x="116" y="75" width="4" height="6" rx="2" fill="#ff8c00" />
                <rect x="80" y="38" width="22" height="16" rx="3" fill="#d0e2ff" />
                <circle cx="84" cy="44" r="2" fill="#7aadff" />
                <circle cx="90" cy="44" r="2" fill="#7aadff" />
                <circle cx="96" cy="44" r="2" fill="#7aadff" />
              </svg>
            </div>
            <p className="mcd-empty-title">No data</p>
            <p className="mcd-empty-sub">Your recent chat files will be here.</p>
          </div>
        ) : (
          <>
            {/* Images grid */}
            {(activeFilter === 'all' || activeFilter === 'media') && (() => {
              const mediaFiles = displayed.filter(f => f.type === 'image' || f.type === 'video');
              if (!mediaFiles.length) return null;
              return (
                <div className="mcd-section">
                  {activeFilter === 'all' && <h3 className="mcd-section-title">Photos & Videos</h3>}
                  <div className="mcd-media-grid">
                    {mediaFiles.map(f => (
                      <button
                        key={f.id}
                        className="mcd-media-thumb"
                        onClick={() => handleFileClick(f)}
                        type="button"
                        aria-label={f.fileName}
                      >
                        <img src={getFileUrl(f.fileUrl)} alt={f.fileName} />
                        {f.type === 'video' && (
                          <div className="mcd-play-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                          </div>
                        )}
                        <span className="mcd-media-date">{formatDate(f.uploadedAt)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* File list */}
            {(activeFilter === 'all' || activeFilter === 'file') && (() => {
              const docFiles = displayed.filter(f => f.type === 'file');
              if (!docFiles.length) return null;
              return (
                <div className="mcd-section">
                  {activeFilter === 'all' && <h3 className="mcd-section-title">Files</h3>}
                  <div className="mcd-file-list">
                    {docFiles.map(f => (
                      <button
                        key={f.id}
                        className="mcd-file-row"
                        onClick={() => handleFileClick(f)}
                        type="button"
                      >
                        <div className="mcd-file-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <div className="mcd-file-info">
                          <span className="mcd-file-name">{f.fileName}</span>
                          <span className="mcd-file-meta">
                            {formatDate(f.uploadedAt)}{f.size ? ` · ${formatSize(f.size)}` : ''}
                          </span>
                        </div>
                        <div className="mcd-file-dl">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default MobileCloudDrivePage;
